import { UIElement } from 'api/moduleElement';
import { AiScribeForm } from 'components/panels/AiScribeForm';
import { ModuleContext } from 'components/utils/module/ModuleContext';
import { SlidePanel } from 'components/utils/panels/AvoSlidePanel';
import { Body2, H4 } from 'components/utils/typo';
import { useTriggers } from 'hooks/module/resources/useTriggers';
import { useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DraggableContext, ModuleElementContext } from '../ModuleBoard';
import { GroupCardContext } from './GroupCard';
import { CardContainer } from './utils/CardContainer';

export const AiScribeTypes = [
  { value: 'H_N_P', label: 'H&P', disabled: false },
  { value: 'SIGN_OUT', label: 'Signout', disabled: false },
] as const;

export type AiScribeTypeValue = (typeof AiScribeTypes)[number]['value'];

interface AiScribeCardProps {
  element: UIElement;
}

export interface AiScribeFormVariable {
  module: number;
  trigger: number;
  title: string;
  subtitle: string;
  container?: number;
  position: number;
  variable: string;
  is_optional: boolean;
  type: AiScribeTypeValue;
}

export const AiScribeCard = ({ element }: AiScribeCardProps) => {
  const { module } = useContext(ModuleContext);
  const { setIsDragDisabled } = useContext(DraggableContext);
  const { groupCardId } = useContext(GroupCardContext);
  const [formOpen, setFormOpen] = useState(!element.id);
  useEffect(() => setIsDragDisabled(formOpen), [formOpen]);

  const { data: triggers } = useTriggers();

  const alwayOnTrigger = triggers.find((data) => data.title === 'Always On');

  const defaultValues: AiScribeFormVariable = {
    module: module!.id,
    trigger: element.trigger ? element.trigger.id : alwayOnTrigger?.id,
    title: element.title || '',
    subtitle: element.subtitle || '',
    container: groupCardId,
    position: element.position,
    variable: element.variable_item?.name || '',
    is_optional: element.is_optional,
    type: element.type ?? AiScribeTypes[0].value,
  };

  const form = useForm<AiScribeFormVariable>({ defaultValues });

  const { watch, handleSubmit } = form;
  const { refetchModuleElements, clearModuleElements } = useContext(ModuleElementContext);
  const onCancel = () => {
    clearModuleElements();
    form.reset(defaultValues);
    setFormOpen(false);
  };

  const onSave = () => {
    try {
      refetchModuleElements();
      setFormOpen(false);
    } catch (e) {
      console.log(e);
    }
  };
  return (
    <FormProvider {...form}>
      <CardContainer
        elementId={element.id}
        onOpenPanel={() => setFormOpen(true)}
        isPreviewing={formOpen}
        variables={[{ type: 'AI Output', name: form.watch('variable') }]}
      >
        <div className='flex gap-1'>
          <H4 className='text-gray-900'>{form.watch('title')}</H4>
          {form.watch('is_optional') && <Body2 className='text-gray-700'>(optional)</Body2>}
        </div>
        <Body2 className='text-gray-900'>{watch('subtitle')}</Body2>
      </CardContainer>

      <SlidePanel open={formOpen}>
        <AiScribeForm aiScribeId={element.id} onSave={onSave} onCancel={onCancel} />
      </SlidePanel>
    </FormProvider>
  );
};
